
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    DashboardIcon,
    LearningIcon,
    AssessmentIcon,
    CogIcon,
    XMarkIcon,
    ShieldCheckIcon,
    PencilIcon,
    TargetIcon,
    BriefcaseIcon,
    ClipboardDocumentCheckIcon,
} from './icons';
import { useAuth } from '../contexts/AuthContext';
import { useSidebarLogic } from '../hooks/useSidebarLogic';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useOrgTree } from '../hooks/useOrgTree';
import { cn } from '../utils/cn';
import ConfirmationModal from './ConfirmationModal';
import { NotificationBell } from './NotificationBell';
import { useFocusTrap } from '../hooks/useFocusTrap';
import CustomSelect from './CustomSelect';
import { Logo } from './Logo';
interface SidebarProps {
    activeView: string; // Now passes pathname (e.g., '/dashboard')
    onViewChange: (path: string) => void;
    mobileOpen?: boolean;
    onMobileClose?: () => void;
    prioritizationEnabled: boolean;
    contentManagementEnabled: boolean;
    taskManagementEnabled: boolean;
    isMfaEnforced?: boolean;
    /** Live list of languages the admin has enabled for this tenant */
    availableLanguages?: string[];
}

const UserGroupIcon = ({ className }: { className?: string }) => (
    <svg className={cn("pointer-events-none", className)} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
);

const Sidebar: React.FC<SidebarProps> = ({ activeView, onViewChange, mobileOpen,
    onMobileClose,
    prioritizationEnabled = false,
    contentManagementEnabled = false,
    taskManagementEnabled = false,
    isMfaEnforced = false,
    availableLanguages
}) => {
    const { t } = useTranslation();
    const isOnline = useOnlineStatus();
    const { isSuperAdmin, isAssessor, userProfile, currentUser, permissions } = useAuth();

    // Use Decoupled Logic
    const {
        showProfileModal,
        isSaving,
        mfaLoading,
        isMfaActive,
        handleLogout,
        openProfileModal,
        closeProfileModal,
        handleSaveProfile,
        handleToggleMfa,
        showMfaDeactivateModal,
        closeMfaDeactivateModal,
        handleConfirmDisableMfa,
        showMfaDeactivateSuccess,
        closeMfaDeactivateSuccess
    } = useSidebarLogic(onViewChange);

    const profileModalRef = React.useRef<HTMLDivElement>(null);
    useFocusTrap(profileModalRef, showProfileModal);

    const canDisableMfa = isMfaActive && (!isMfaEnforced || isSuperAdmin);

    const profileSchema = z.object({
        displayName: z.string().min(1, t('validation.nameRequired', 'Name ist erforderlich')),
        language: z.string().optional(),
        emailPreferences: z.object({
            commentNotifications: z.enum(['immediate', 'daily', 'none']).optional(),
            taskReminders: z.boolean().optional()
        }).optional()
    });

    const effectiveLanguages = isSuperAdmin
        ? ['de', 'en', 'fr', 'it']
        : (availableLanguages?.length ? availableLanguages : (userProfile?.tenantConfig?.availableLanguages || ['de', 'en', 'fr', 'it']));

    // If the saved language is no longer available, fall back to the first allowed language or i18n.language
    const resolveLanguage = (lang: string | undefined) => {
        if (!lang) return effectiveLanguages[0] || 'de';
        if (effectiveLanguages.includes(lang)) return lang;
        return effectiveLanguages[0] || 'de';
    };

    const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
        resolver: zodResolver(profileSchema),
        defaultValues: {
            displayName: userProfile?.displayName || currentUser?.displayName || '',
            language: resolveLanguage(userProfile?.language),
            emailPreferences: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                commentNotifications: userProfile?.emailPreferences?.commentNotifications || ((userProfile as any)?.emailNotifications === false ? 'none' : 'immediate'),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                taskReminders: userProfile?.emailPreferences?.taskReminders ?? ((userProfile as any)?.emailNotifications !== false)
            }
        }
    });

    React.useEffect(() => {
        if (showProfileModal) {
            reset({
                displayName: userProfile?.displayName || currentUser?.displayName || '',
                language: resolveLanguage(userProfile?.language),
                emailPreferences: {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    commentNotifications: userProfile?.emailPreferences?.commentNotifications || ((userProfile as any)?.emailNotifications === false ? 'none' : 'immediate'),
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    taskReminders: userProfile?.emailPreferences?.taskReminders ?? ((userProfile as any)?.emailNotifications !== false)
                }
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showProfileModal, userProfile, currentUser, reset]);

    // Definition der Menüpunkte
    const { getOrgUnit } = useOrgTree(isSuperAdmin || isAssessor ? undefined : userProfile?.tenantId);
    const ownUnitName = permissions.orgUnitId ? getOrgUnit(permissions.orgUnitId)?.name : undefined;
    const roleCaption = isSuperAdmin
        ? 'super admin'
        : isAssessor
            ? t('userManagement.roles.externalAssessor', 'Externer Assessor')
            : ownUnitName;

    const isTenantAdminOrHigher = permissions.isTenantAdmin;
    const isOrgAdminOrHigher = isTenantAdminOrHigher || permissions.isOrgAdmin;
    const isQmOrManagement = permissions.canUseCaf;
    const canUseProjects = permissions.canUseProjects;

    const menuItems = {
        superAdmin: {
            path: '/super-admin',
            label: t('sidebar.superAdmin'),
            icon: ShieldCheckIcon,
            visible: isSuperAdmin
        },
        learning: [
            { path: '/learning', label: t('sidebar.learningHub'), icon: LearningIcon, visible: !isSuperAdmin && !isAssessor && isQmOrManagement },
        ],
        projectManagement: [
            { path: '/portfolio', label: t('sidebar.portfolio', 'Projektportfolio'), icon: BriefcaseIcon, visible: !isSuperAdmin && !isAssessor && canUseProjects },
            // Auch ohne Projektrolle: persönlich zugewiesene Aufgaben (docs/07, 5.3)
            { path: '/my-tasks', label: t('myTasks.title'), icon: ClipboardDocumentCheckIcon, visible: !isSuperAdmin && !isAssessor && taskManagementEnabled },
        ],
        assessment: [
            { path: '/dashboard', label: t('sidebar.dashboard'), icon: DashboardIcon, visible: !isSuperAdmin && isQmOrManagement },
            { path: '/assessment', label: isAssessor ? t('assessment.externalAssessment') : t('sidebar.assessments'), icon: AssessmentIcon, visible: !isSuperAdmin && (isAssessor || isQmOrManagement) },
            { path: '/prioritization', label: t('sidebar.prioritization'), icon: TargetIcon, visible: !isSuperAdmin && !isAssessor && prioritizationEnabled && isQmOrManagement },
        ],
        admin: [
            { path: '/admin/content', label: isSuperAdmin ? t('sidebar.globalTemplates', 'Globale Vorlagen') : t('sidebar.contentManagement'), icon: PencilIcon, visible: (isSuperAdmin || (isTenantAdminOrHigher && contentManagementEnabled)) && !isAssessor },
            { path: '/admin/users', label: t('sidebar.user'), icon: UserGroupIcon, visible: isOrgAdminOrHigher && !isSuperAdmin && !isAssessor },
            // Portfoliomanagement pflegt hier die Kategorien der eigenen Einheit
            { path: '/admin/settings', label: t('sidebar.settings'), icon: CogIcon, visible: (isTenantAdminOrHigher || permissions.canManagePortfolio) && !isSuperAdmin && !isAssessor },
        ]
    };

    const visibleLearningItems = menuItems.learning.filter(item => item.visible);
    const visibleProjectManagementItems = menuItems.projectManagement.filter(item => item.visible);
    const visibleAssessmentItems = menuItems.assessment.filter(item => item.visible);
    const visibleAdminItems = menuItems.admin.filter(item => item.visible);

    const isActive = (path: string) => activeView === path;

    interface NavItem {
        path: string;
        label: string;
        icon: React.ElementType;
        visible: boolean;
    }

    const renderNavButton = (item: NavItem) => (
        <button
            key={item.path}
            type="button"
            onClick={() => onViewChange(item.path)}
            className={cn(
                "w-full flex items-center justify-start text-left space-x-3 px-4 py-3 rounded-lg transition-all duration-200 group",
                isActive(item.path)
                    ? (isSuperAdmin ? 'bg-indigo-600 text-white font-medium shadow-md shadow-indigo-200' : 'bg-blue-600 text-white font-medium shadow-md shadow-blue-200')
                    : (isSuperAdmin ? 'text-gray-600 hover:bg-indigo-50 hover:text-indigo-600' : 'text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm')
            )}
        >
            <item.icon className={cn("w-5 h-5 flex-shrink-0", isActive(item.path) ? 'text-white' : 'text-gray-400 group-hover:text-current')} />
            <span>{item.label}</span>
        </button>
    );

    return (
        <>
            {/* Mobile Overlay Backdrop */}
            {mobileOpen && (
                <>
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                    <div
                        className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity"
                        onClick={onMobileClose}
                    />
                </>
            )}

            {/* Sidebar Container */}
            <aside className={cn(
                "bg-white border-r border-gray-200 flex flex-col shadow-xl h-full fixed lg:static inset-y-0 left-0 z-50 w-72 transition-transform duration-300 ease-in-out",
                mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
            )} aria-label={t('common.sidebarNavigation', 'Seitennavigation')}>

                {/* Close Button for Mobile */}
                {mobileOpen && (
                    <button
                        onClick={onMobileClose}
                        className="absolute top-4 right-4 p-2 bg-gray-100 rounded-full text-gray-500 hover:text-gray-800 lg:hidden"
                        aria-label={t('modals.close')}
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                )}

                {/* Sidebar Header */}
                <div className="px-6 py-6 border-b border-gray-100 bg-white">
                    <div className="flex items-center gap-3">
                        {!isSuperAdmin && (
                            <Logo />
                        )}

                        <div className="flex flex-col">
                            <h1 className="text-xl font-bold text-gray-900 tracking-tight leading-tight">{t('sidebar.title')}</h1>
                            {roleCaption && (
                                <span className={cn(
                                    "text-[10px] uppercase tracking-widest font-bold w-fit",
                                    isSuperAdmin ? 'text-indigo-600' : 'text-blue-600'
                                )}>
                                    {roleCaption}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <nav className="flex-1 p-4 space-y-1 overflow-y-auto bg-gray-50/50" aria-label={t('common.mainNavigation', 'Hauptnavigation')}>
                    {menuItems.superAdmin.visible && renderNavButton(menuItems.superAdmin)}

                    {/* Lern-Hub – kein Sektions-Label */}
                    {visibleLearningItems.length > 0 && (
                        <>
                            {visibleLearningItems.map(renderNavButton)}
                        </>
                    )}

                    {/* Projektmanagement */}
                    {visibleProjectManagementItems.length > 0 && (
                        <>
                            <div className="text-xs font-bold text-gray-600 uppercase tracking-wider px-4 pb-1 pt-3">{t('sidebar.projectManagement')}</div>
                            {visibleProjectManagementItems.map(renderNavButton)}
                        </>
                    )}

                    {/* Bewertung */}
                    {visibleAssessmentItems.length > 0 && (
                        <>
                            <div className="text-xs font-bold text-gray-600 uppercase tracking-wider px-4 pb-1 pt-3">{t('sidebar.assessmentSection')}</div>
                            {visibleAssessmentItems.map(renderNavButton)}
                        </>
                    )}

                    {/* Admin */}
                    {visibleAdminItems.length > 0 && (
                        <>
                            <div className="text-xs font-bold text-gray-600 uppercase tracking-wider px-4 pb-1 pt-3">{t('sidebar.admin')}</div>
                            {visibleAdminItems.map(renderNavButton)}
                        </>
                    )}
                </nav>

                <div className="p-4 border-t border-gray-200 bg-white">
                    <div className="flex items-center p-3 bg-gray-50 rounded-xl border border-gray-100 mb-3 relative group">
                        <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0",
                            isSuperAdmin ? 'bg-indigo-100 text-indigo-700' : 'bg-blue-100 text-blue-700'
                        )}>
                            {userProfile?.displayName ? userProfile.displayName.charAt(0).toUpperCase() : (userProfile?.email?.charAt(0).toUpperCase() || 'U')}
                        </div>
                        <div className="flex-1 overflow-hidden">
                            <div className="text-sm font-medium text-gray-900 truncate" title={userProfile?.displayName || ((userProfile?.email || ''))}>{userProfile?.displayName || userProfile?.email}</div>
                            <div className={cn('text-xs truncate', isOnline ? 'text-gray-500' : 'font-medium text-amber-800')} role="status">
                                {isOnline ? t('sidebar.online') : t('sidebar.offline', 'Offline')}
                            </div>
                        </div>

                        <div className="flex items-center gap-1">
                            <NotificationBell />
                            <button
                                onClick={openProfileModal}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:opacity-100"
                                title={t('sidebar.editProfile')}
                                aria-label={t('sidebar.editProfile')}
                            >
                                <PencilIcon className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center space-x-2 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 py-2.5 rounded-lg transition-colors font-medium"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        <span>{t('sidebar.logout')}</span>
                    </button>
                </div>
            </aside>

            {/* Profile Edit Modal */}
            {showProfileModal && (
                <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-fade-in">
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                    <div 
                        ref={profileModalRef} 
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" 
                        role="dialog" 
                        aria-modal="true" 
                        aria-labelledby="profile-modal-title"
                        tabIndex={-1}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                e.stopPropagation();
                                closeProfileModal();
                            }
                        }}
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h2 id="profile-modal-title" className="text-lg font-bold text-gray-900">{t('modals.editProfileTitle')}</h2>
                            <button onClick={closeProfileModal} className="text-gray-400 hover:text-gray-600" aria-label={t('modals.close')}>
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit(handleSaveProfile)} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold uppercase text-gray-500 mb-1">{t('userManagement.displayName')}</label>
                                <input
                                    type="text"
                                    {...register('displayName')}
                                    className={cn(
                                        "w-full border rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500 bg-white text-gray-900",
                                        errors.displayName ? "border-red-500" : "border-gray-300"
                                    )}
                                    placeholder={t('sidebar.ihrName')}
                                    aria-invalid={!!errors.displayName}
                                    aria-describedby={errors.displayName ? "displayName-error" : undefined}
                                />
                                {errors.displayName && (
                                    <p id="displayName-error" className="text-xs text-red-500 mt-1" role="alert">
                                        {errors.displayName.message as string}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="profile-email" className="block text-xs font-bold uppercase text-gray-500 mb-1">{t('sidebar.eMail')}</label>
                                <input
                                    id="profile-email"
                                    type="email"
                                    value={userProfile?.email || currentUser?.email || ''}
                                    disabled
                                    aria-label={t('sidebar.eMail')}
                                    className="w-full border border-gray-200 rounded-lg p-2.5 text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                                />
                            </div>

                            {/* Language Selection */}
                            <div className="pt-4 border-t border-gray-100 pb-2">
                                <CustomSelect
                                    label={t('sidebar.languageSettings')}
                                    value={watch('language') as string}
                                    onChange={(val) => setValue('language', val as string, { shouldDirty: true, shouldValidate: true })}
                                    options={(isSuperAdmin
                                        ? ['de', 'en', 'fr', 'it']
                                        : (availableLanguages?.length ? availableLanguages : (userProfile?.tenantConfig?.availableLanguages || ['de', 'en', 'fr', 'it']))
                                    ).map((code: string) => ({
                                        value: code,
                                        label: code === 'de' ? 'Deutsch' : code === 'en' ? 'English' : code === 'fr' ? 'Français' : 'Italiano'
                                    }))}
                                />
                            </div>

                            {/* Notifications Section */}
                            <div className="pt-4 border-t border-gray-100">
                                <h3 className="text-sm font-bold text-gray-900 mb-3">{t('sidebar.benachrichtigungen')}</h3>

                                <div className="mb-4">
                                    <label className="block text-xs font-bold uppercase text-gray-500 mb-2">{t('sidebar.kommentareErwaehnungen')}</label>
                                    <div className="space-y-2">
                                        <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 cursor-pointer transition-colors">
                                            <input
                                                type="radio"
                                                value="immediate"
                                                {...register('emailPreferences.commentNotifications')}
                                                className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                            />
                                            <span className="text-sm font-medium text-gray-900">{t('sidebar.sofortBenachrichtigen')}</span>
                                        </label>
                                        <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 cursor-pointer transition-colors">
                                            <input
                                                type="radio"
                                                value="daily"
                                                {...register('emailPreferences.commentNotifications')}
                                                className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                            />
                                            <span className="text-sm font-medium text-gray-900">{t('sidebar.taeglicheZusammenfassung')}</span>
                                        </label>
                                        <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 cursor-pointer transition-colors">
                                            <input
                                                type="radio"
                                                value="none"
                                                {...register('emailPreferences.commentNotifications')}
                                                className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                            />
                                            <span className="text-sm font-medium text-gray-900">{t('sidebar.keineEMails')}</span>
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold uppercase text-gray-500 mb-2">{t('sidebar.erinnerungen')}</label>
                                    <label className="flex items-center gap-3 cursor-pointer group p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                                        <div className="relative flex items-center">
                                            <input
                                                type="checkbox"
                                                {...register('emailPreferences.taskReminders')}
                                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-sm font-medium text-gray-900">{t('sidebar.faelligeAufgabenMassnahmen')}</span>
                                            <span className="text-xs text-gray-500">{t('sidebar.erhaltenSieEMailsWenn')}</span>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* MFA Section — hidden for SSO users (IdP handles MFA) */}
                            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                            {!currentUser?.providerData?.some((p: any) => p.providerId?.startsWith('saml.') || p.providerId?.startsWith('oidc.')) && (
                            <div className="pt-4 border-t border-gray-100">
                                <h3 className="text-sm font-bold text-gray-900 mb-3">{t('sidebar.security')}</h3>
                                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <ShieldCheckIcon className={cn("w-5 h-5", isMfaActive ? 'text-green-600' : 'text-gray-400')} />
                                            <span className="text-sm font-medium text-gray-900">{t('sidebar.twoFactorAuth')}</span>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1 pl-7">
                                            {isMfaActive
                                                ? (canDisableMfa ? t('sidebar.mfaStatus.active') : t('sidebar.mfaStatus.activeEnforced'))
                                                : t('sidebar.mfaStatus.notSetup')
                                            }
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => handleToggleMfa(isMfaEnforced)}
                                        disabled={(isMfaActive && !canDisableMfa) || mfaLoading}
                                        className={cn(
                                            "px-3 py-1.5 rounded-lg text-xs font-bold transition-colors border",
                                            isMfaActive
                                                ? 'bg-white border-gray-300 text-red-600 hover:bg-red-50 disabled:bg-gray-50 disabled:text-gray-400'
                                                : 'bg-indigo-600 border-transparent text-white hover:bg-indigo-700'
                                        )}
                                    >
                                        {mfaLoading ? '...' : (isMfaActive ? t('sidebar.mfaStatus.disable') : t('sidebar.mfaStatus.setup'))}
                                    </button>
                                </div>
                            </div>
                            )}

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={closeProfileModal}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium text-sm"
                                >
                                    {t('modals.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 shadow-sm disabled:opacity-50"
                                >
                                    {isSaving ? t('common.saving') : t('modals.save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MFA Deactivation Confirmation Modal */}
            <ConfirmationModal
                isOpen={showMfaDeactivateModal}
                onClose={closeMfaDeactivateModal}
                onConfirm={handleConfirmDisableMfa}
                title={t('sidebar.mfaDeaktivieren')}
                message={t('sidebar.mfaDisableConfirmMessage')}
                confirmButtonText={t('sidebar.mfaDisableConfirmButton')}
                confirmButtonClass="bg-red-600 hover:bg-red-700 focus:ring-red-500"
                isLoading={mfaLoading}
                icon={ShieldCheckIcon}
                iconClassName="text-red-600"
                iconBgClassName="bg-red-100"
                zIndex={70}
            />

            {/* MFA Deactivation Success Modal */}
            <ConfirmationModal
                isOpen={showMfaDeactivateSuccess}
                onClose={closeMfaDeactivateSuccess}
                onConfirm={closeMfaDeactivateSuccess}
                title={t('sidebar.mfaDeaktiviert')}
                message={t('sidebar.mfaDisableSuccessMessage')}
                confirmButtonText="OK"
                hideCancel={true}
                icon={ShieldCheckIcon}
                iconClassName="text-green-600"
                iconBgClassName="bg-green-100"
                zIndex={70}
            />
        </>
    );
};

export default Sidebar;
